import { FeatureFlagsContext } from '@/common/featureFlagsWrapper';
import { useAccess } from '@/common/hooks/useAccess';
import {
  Box,
  Button,
  CircularProgress,
  InputBase,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAdmin, isManager } from '../../common/actionHelper';
import { Resource } from '../../common/constants/resource';
import { NoraPermissionV2, Roles } from '../../common/constants/roles';
import { FetchService } from '../../common/fetch.service';
import { EditPeerReviewModal } from './EditPeerReviewModal';
import { IUnderwriter } from './types';
import { UnderwriterTable } from './UnderwriterTable';

export function PeerReviewPage() {
  const { flags } = useContext(FeatureFlagsContext);
  const navigate = useNavigate();
  const canAccessPeerReviewPage = useAccess({
    permission: NoraPermissionV2.VIEW_CONTROL_PANEL,
    legacyCheck: r =>
      isAdmin(r) ||
      (isManager(r) && flags.controlPanelUnderwritingManagerEnabled),
  });

  const [selectedUnderwriter, setSelectedUnderwriter] =
    useState<IUnderwriter | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const { data: underwriters = [], isLoading: isLoadingUnderwriters } =
    useQuery<IUnderwriter[]>({
      queryKey: ['underwriters'],
      queryFn: () => FetchService.getInstance().get('/users/underwriters'),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });

  const juniorUnderwriters = useMemo(() => {
    return underwriters.filter(uw =>
      uw.roles.includes(Roles.UW_JUNIOR_UNDERWRITER),
    );
  }, [underwriters]);

  const nonJuniorUnderwriters = useMemo(() => {
    return underwriters.filter(
      uw => !uw.roles.includes(Roles.UW_JUNIOR_UNDERWRITER),
    );
  }, [underwriters]);

  const filteredUnderwriters = useMemo(() => {
    if (!searchTerm) return juniorUnderwriters;

    const searchLower = searchTerm.toLowerCase();
    return juniorUnderwriters.filter(
      uw =>
        uw.firstName?.toLowerCase().includes(searchLower) ||
        uw.lastName?.toLowerCase().includes(searchLower),
    );
  }, [juniorUnderwriters, searchTerm]);

  if (!canAccessPeerReviewPage) {
    return (
      <Box
        sx={{
          paddingInline: 6,
          paddingBlock: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="h1">
          You are not authorized to access this page
        </Typography>
        <Box sx={{ width: 250 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              navigate('/');
            }}
          >
            Go to Dashboard
          </Button>
        </Box>
      </Box>
    );
  }

  if (isLoadingUnderwriters) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          paddingInline: 6,
          paddingBlock: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="h1">Loading...</Typography>
      </Box>
    );
  }

  if (!juniorUnderwriters || juniorUnderwriters.length === 0) {
    return (
      <Box>
        <Typography variant="h1">No underwriters found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ paddingInline: 6, paddingBlock: 2 }}>
      <Typography variant="h1" sx={{ fontSize: '1.75rem', fontWeight: 600 }}>
        {Resource.PEER_REVIEW}
      </Typography>
      <Typography variant="body2" sx={{ mb: 4 }}>
        {Resource.PEER_REVIEW_DESCRIPTION}
      </Typography>

      <Box
        sx={{
          borderRadius: 2,
          border: '1px solid var(--neutrals-night-20)',
          width: 250,
          mb: 2,
        }}
      >
        <InputBase
          placeholder="Search by underwriter name.."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          sx={{ width: 250, px: 1, fontWeight: 400, fontSize: '0.9rem' }}
        />
      </Box>

      <UnderwriterTable
        underwriters={filteredUnderwriters}
        onEditUnderwriter={setSelectedUnderwriter}
      />

      {selectedUnderwriter && (
        <EditPeerReviewModal
          open={!!selectedUnderwriter}
          onClose={() => setSelectedUnderwriter(null)}
          underwriter={selectedUnderwriter}
          peerReviewers={nonJuniorUnderwriters}
        />
      )}
    </Box>
  );
}
